// Per-voice level meter, derived from the hap stream.
//
// We can't tap individual voice nodes — Strudel routes everything
// through one master out — so this is an *approximation*: every hap
// onset starts an exponential envelope at peak = hap.value.gain, and
// the meter shows the max envelope contribution among all haps the
// voice has fired so far. Decays at ~30 dB/sec (-1.5 in log10 amplitude
// per second of real time), which feels like a fast PPM meter.
//
// Visually correlates with what you hear because the hap stream is the
// same source that drives playback. Trails the audio by a few ms (the
// scheduler's lookahead window), which matches the existing
// flare-on-VoiceTrace timing.

import { memo, useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { subscribeTick } from '../../strudel/sharedTicker';
import type { HapRefs } from '../../strudel/useHaps';
import { colorForVoice } from './palette';

type Props = {
  voice: string;
  refs: HapRefs;
  nowRef: MutableRefObject<number>;
  width?: number;
  height?: number;
};

// 30 dB/sec → amplitude factor ~0.0316 per second.
const DECAY_PER_SEC = -1.5; // log10 amplitude
const PEAK_HOLD_MS = 320;

export const LevelMeter = memo(LevelMeterInner);

function LevelMeterInner({ voice, refs, nowRef, width = 36, height = 6 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    const w = canvas.width;
    const h = canvas.height;
    const color = colorForVoice(voice);
    let peakHold = 0;
    let peakHoldUntilMs = 0;

    return subscribeTick((nowMs) => {
      const now = nowRef.current;
      const cps = refs.cps.current || 0.5;
      const haps = refs.haps.current;

      // Walk every hap that's already begun and find the loudest current
      // envelope contribution. O(N) per frame where N <= ~30 in steady
      // state — a sub-millisecond cost.
      let level = 0;
      for (const hap of haps) {
        if (hap.voice !== voice) continue;
        const dt = now - hap.begin;
        if (dt < 0) continue; // not yet
        const dtSec = dt / cps;
        const peak = clampGain(hap.value.gain);
        const env = peak * Math.pow(10, DECAY_PER_SEC * dtSec);
        if (env > level) level = env;
      }
      if (level <= 0.001 && peakHold <= 0.001) {
        // Cheap idle path: clear once and skip the paint.
        ctx.clearRect(0, 0, w, h);
        ctx.fillStyle = 'rgba(63, 63, 70, 0.45)';
        ctx.fillRect(0, h / 2 - dpr / 2, w, dpr);
        return;
      }
      if (level >= peakHold) {
        peakHold = level;
        peakHoldUntilMs = nowMs + PEAK_HOLD_MS;
      } else if (nowMs > peakHoldUntilMs) {
        // After the hold, decay the peak at the same rate as the level.
        peakHold = peakHold * Math.pow(10, DECAY_PER_SEC / 60);
      }

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = 'rgba(39, 39, 42, 0.85)';
      ctx.fillRect(0, 0, w, h);
      const fillW = Math.max(0, Math.min(1, level)) * w;
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.85;
      ctx.fillRect(0, 0, fillW, h);
      ctx.globalAlpha = 1;
      // Peak indicator — single pixel column at the held maximum.
      const peakX = Math.max(0, Math.min(w - dpr, peakHold * w - dpr));
      ctx.fillStyle = 'rgba(253, 224, 71, 0.95)';
      ctx.fillRect(peakX, 0, dpr, h);
    });
  }, [voice, refs, nowRef, width, height]);

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={`${voice} level`}
      style={{ width, height }}
      className="block"
    />
  );
}

function clampGain(v: unknown): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : 0.8;
  return Math.min(1, Math.max(0, n));
}
